// 📄 plantillaReferenciaService.ts
// Plantillas de referencia del "Validador de Estructura". Igual que
// clienteConexionService.ts, nunca toca Firestore directo — todo pasa por
// onehubApi (/plantillas-referencia), protegido por el mismo middleware de
// auth (ver functions/routes/plantillasReferencia.js).
import { getAuth } from "firebase/auth";
import { RestriccionTag } from "../utils/xmlStructureUtils.ts";

// "proyecto" = plantilla asociada a un projectId puntual (antes era
// "cliente" = toda la organización); "global" = estándar para todos,
// gestionada solo desde el panel Admin.
export type AlcancePlantilla = "global" | "proyecto";

// Metadatos de trazabilidad CIG — todos opcionales (ver
// PlantillaMetadatosForm.tsx, componente compartido entre los 3 modales
// que los editan). documentType/documentStage/documentStandard van en
// inglés porque son valores técnicos tal como los usa CIG.
export interface PlantillaMetadatos {
  documentType?: string;
  documentStage?: string;
  documentStandard?: string;
  sistemaOrigen?: string;
  sistemaDestino?: string;
  descripcion?: string;
}

export interface PlantillaReferencia extends PlantillaMetadatos {
  // Etiqueta legible, separada del id de Firestore (tipoDocumento) — antes
  // solo existía a nivel proyecto; ahora también a nivel global, desde que
  // el id global lleva sufijo de Document Stage (ver plantillaIdUtils.ts).
  // Documentos guardados antes de que existiera este campo no lo tienen
  // ("" desde el backend) — la UI cae a tipoDocumento como fallback.
  nombre?: string;
  nombreArchivo: string;
  contenidoXML: string;
  subidoPor: string;
  fecha: any;
  restricciones?: RestriccionTag[];
}

export interface PlantillaResuelta {
  encontrada: boolean;
  alcance?: AlcancePlantilla;
  plantilla?: PlantillaReferencia;
}

export interface PlantillaGlobal extends PlantillaReferencia {
  tipoDocumento: string;
}

// Resumen de una entrada del dropdown del Validador — solo lo que hace
// falta para el label enriquecido (ver construirLabelTipo en
// ValidadorEstructura.tsx); sistemaOrigen/sistemaDestino no viajan acá
// porque el dropdown no los muestra.
export interface TipoDocumentoResumen {
  tipoDocumento: string;
  nombre?: string;
  documentType?: string;
  documentStage?: string;
  documentStandard?: string;
  descripcion?: string;
}

// Tipos disponibles para el dropdown del Validador, ya separados por
// origen — ver TiposDocumentoSeparados en plantillasReferenciaService.js.
export interface TiposDocumentoSeparados {
  globales: TipoDocumentoResumen[];
  proyecto: TipoDocumentoResumen[];
}

// Fila de la lista de "Configuración > Plantillas XML" — sin contenidoXML
// a propósito (ver listarProyecto en el backend), la lista solo necesita
// metadata. `nombre` es la etiqueta editable ("renombrar"); `tipoDocumento`
// es el id real usado para matchear en el Validador y nunca se edita acá.
export interface PlantillaProyecto {
  tipoDocumento: string;
  nombre: string;
  nombreArchivo: string;
  subidoPor: string;
  fecha: any;
}

const getToken = async (): Promise<string> => {
  const user = getAuth().currentUser;
  if (!user) throw new Error("No se detectó una sesión activa. Por favor, reingresa al sistema.");
  return user.getIdToken();
};

const post = async (path: string, body: object) => {
  const token = await getToken();
  const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify(body)
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error ${response.status} al comunicarse con el servidor.`);
  }
  return data;
};

export const listarTiposDocumento = async (projectId?: string): Promise<TiposDocumentoSeparados> => {
  const { globales, proyecto } = await post("/plantillas-referencia/tipos", { projectId });
  return { globales, proyecto };
};

export const resolverPlantilla = async (tipoDocumento: string, projectId?: string): Promise<PlantillaResuelta> => {
  return post("/plantillas-referencia/resolver", { projectId, tipoDocumento });
};

export const existePlantilla = async (tipoDocumento: string, alcance: AlcancePlantilla, projectId?: string): Promise<boolean> => {
  const { existe } = await post("/plantillas-referencia/existe", { projectId, tipoDocumento, alcance });
  return existe;
};

// `restricciones` debe ir con el snapshot COMPLETO vigente, no solo lo que
// cambió — el backend sobrescribe el documento entero (ver comentario en
// plantillasReferenciaService.js). Para tocar solo restricciones sin
// volver a mandar el XML, usar guardarRestricciones más abajo.
export const guardarPlantilla = async (
  tipoDocumento: string,
  nombreArchivo: string,
  contenidoXML: string,
  alcance: AlcancePlantilla,
  projectId?: string,
  nombre?: string,
  restricciones?: RestriccionTag[],
  metadatos?: PlantillaMetadatos
): Promise<void> => {
  await post("/plantillas-referencia/guardar", { projectId, tipoDocumento, nombreArchivo, contenidoXML, alcance, nombre, restricciones, ...metadatos });
};

// Guarda SOLO las restricciones, sin tocar nombreArchivo/contenidoXML/nombre
// — partial update en el backend (.update()), a diferencia de
// guardarPlantilla que sobrescribe todo el documento.
export const guardarRestricciones = async (
  tipoDocumento: string,
  restricciones: RestriccionTag[],
  alcance: AlcancePlantilla,
  projectId?: string
): Promise<void> => {
  await post("/plantillas-referencia/restricciones/guardar", { projectId, tipoDocumento, alcance, restricciones });
};

// ── Panel Admin — gestión de plantillas globales ──────────────────────────

export const listarPlantillasGlobales = async (): Promise<PlantillaGlobal[]> => {
  const { plantillas } = await post("/plantillas-referencia/admin/listar", {});
  return plantillas;
};

export const eliminarPlantillaGlobal = async (tipoDocumento: string): Promise<void> => {
  await post("/plantillas-referencia/admin/eliminar", { tipoDocumento });
};

// ── Configuración del proyecto — gestión de plantillas propias ────────────

export const listarPlantillasProyecto = async (projectId: string): Promise<PlantillaProyecto[]> => {
  const { plantillas } = await post("/plantillas-referencia/proyecto/listar", { projectId });
  return plantillas;
};

export const eliminarPlantillaProyecto = async (projectId: string, tipoDocumento: string): Promise<void> => {
  await post("/plantillas-referencia/proyecto/eliminar", { projectId, tipoDocumento });
};

export const renombrarPlantillaProyecto = async (projectId: string, tipoDocumento: string, nombre: string): Promise<void> => {
  await post("/plantillas-referencia/proyecto/renombrar", { projectId, tipoDocumento, nombre });
};
